import { colorSchemeOptionListAtom } from 'features/generator/contentFormSections/client/ContentDesignForm'
import { atom } from 'jotai'

import {
	primaryColorAtom,
	secondaryColorAtom,
	selectedColorSchemeAtom,
} from '@/content-from-sections/client'
import { TUploadedMedia } from '@/services'
import { getFileFromMedia, isOnServer } from '@/utils'

import {
	imageFileListAtom,
	imageListAtom,
	imagesInfoButtonListAtom,
	imagesInfoDescriptionAtom,
	imagesInfoHeadlineAtom,
	imagesInfoWebsiteAtom,
	shareImageGalleryAtom,
	TQrColorScheme,
	welcomeImageAtom,
	welcomeImageFileAtom,
} from '../../contentFormSections'
import { QR_TYPE } from '../../generatorTypes'

export type TImagesContent = {
	type: QR_TYPE.Images
	colors: Omit<TQrColorScheme, 'name'>
	buttons: { title: string; url: string | undefined }[]
	gallery: TUploadedMedia[]
	info: { headline: string; website: string; description: string }
	isShareable: boolean
	loaderImage: TUploadedMedia
}

export const imagesContentAtom = atom<TImagesContent, [TImagesContent], void>(
	(get) => ({
		type: QR_TYPE.Images as const,
		colors: {
			primary: get(primaryColorAtom),
			secondary: get(secondaryColorAtom),
		},
		buttons: get(imagesInfoButtonListAtom),
		gallery: get(imageListAtom),
		info: {
			headline: get(imagesInfoHeadlineAtom),
			website: get(imagesInfoWebsiteAtom) || '',
			description: get(imagesInfoDescriptionAtom) || '',
		},
		isShareable: get(shareImageGalleryAtom),
		loaderImage:
			get(welcomeImageAtom) ||
			({
				publicId: '',
				resourceType: '',
				format: '',
				bytes: 0,
			} as TUploadedMedia),
	}),
	(get, set, content) => {
		// Color scheme
		const colorScheme = get(colorSchemeOptionListAtom).find(
			(scheme) =>
				scheme.primary === content.colors.primary &&
				scheme.secondary === content.colors.secondary,
		)

		set(selectedColorSchemeAtom, colorScheme?.name ?? '')
		set(primaryColorAtom, content.colors.primary)
		set(secondaryColorAtom, content.colors.secondary)

		set(imagesInfoButtonListAtom, content.buttons)

		// Images
		set(imageListAtom, content.gallery)
		if (!isOnServer) {
			setTimeout(async () => {
				try {
					const imageFileList = await Promise.all(
						content.gallery.map(async (image) => await getFileFromMedia(image)),
					)
					set(imageFileListAtom, imageFileList)
				} catch (error) {
					console.error(error)
				}
			}, 0)
		}
		// Images info
		set(imagesInfoHeadlineAtom, content.info.headline)
		set(imagesInfoWebsiteAtom, content.info.website)
		set(imagesInfoDescriptionAtom, content.info.description)

		// Is Shareable
		set(shareImageGalleryAtom, content.isShareable)

		// Welcome screen
		set(welcomeImageAtom, content.loaderImage)
		if (!isOnServer) {
			setTimeout(async () => {
				try {
					const loaderFile = await getFileFromMedia(content.loaderImage)
					set(welcomeImageFileAtom, loaderFile)
				} catch (error) {
					console.error(error)
				}
			}, 0)
		}
	},
)
